import $ from 'jquery';
import _ from 'underscore';
import URI from 'urijs';

import { INotification, ScopeLevel, NotificationLevel, TargetComponent, DismissMode, IContext } from '../../types';
import { shouldRender } from '../../utils';
import { mockTopBannerResponse } from './mock-cc-notifications';

interface ICCUNQNotification extends INotification {
  alertType: 'UnifiedQueueAlert' | 'BannerAlert';
}

interface ICCBannerNotification {
  alertType: 'UnifiedQueueAlert' | 'BannerAlert';
  NotificationId: string;
  NotificationMessage: string;
  NotificationLevel: 'danger' | 'warning' | 'info' | 'success';
  ActionTriggered: boolean;
}

type ICCNotification = ICCBannerNotification | ICCUNQNotification;

const notificationLevelMapping = {
  danger: NotificationLevel.Error,
  warning: NotificationLevel.Warning,
  info: NotificationLevel.Info,
  success: NotificationLevel.Confirmation,
};

export class CCNotifications {
  private context: IContext;
  constructor(context: IContext) {
    this.context = context;
  }

  private getUrl() {
    const { Urls } = this.context.getServerContext();
    const ccUrl = Urls.ClientCenterBaseUrl || Urls.CCBaseUrl;

    return ccUrl;
  }

  async getCCNotifications(scopeLevel: ScopeLevel) {
    if (!shouldRender(scopeLevel || ScopeLevel.account, this.context.getChangeScope())) {
      return [];
    }

    const ccNotificaitons: ICCNotification[] = await this.fetchCCNotifications();

    return this.transformCCNotifications(ccNotificaitons);
  }

  async fetchCCNotifications() {
    // MockTopBanner is for test use to speed up the banner loading in Onebox env.
    // When MockTopBanner=true, the page will not send the real request, just return a mock banner immediately.
    const { MockTopBanner } = this.context.getCurrentPermissions().dynamic;

    if (MockTopBanner) {
      const { values: { aid } } = this.context.getCurrentScope();
      const result: ICCBannerNotification[] = aid ? [mockTopBannerResponse as ICCBannerNotification] : [];
      return Promise.resolve(result);
    }

    const { values: { aid, cid, uid } } = this.context.getCurrentScope();
    const aidPart = (_.isNull(aid) || _.isUndefined(aid)) ? '' : `&aid=${aid}`;
    const query = new URI(location.href).search(true);
    const mktPart = query.mkt ? `&mkt=${query.mkt}` : '';

    return $.ajax({
      url: `${this.getUrl()
      }Notifications/StatusNotifications?rurl=${encodeURIComponent(window.location.href)}&cid=${
        cid
      }&uid=${uid}&isUINext=true${aidPart}${mktPart}`,
      xhrFields: {
        withCredentials: true,
      },
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
      },
    });
  }

  buildDismissCCNotification(notification: ICCUNQNotification) {
    const { values: { aid, cid, uid } } = this.context.getCurrentScope();
    const aidPart = (_.isNull(aid) || _.isUndefined(aid)) ? '' : `&aid=${aid}`;

    if ((notification.dismissAction as any) === 'CCUIServerSession') {
      // eslint-disable-next-line no-param-reassign
      notification.dismissAction = (no: INotification) => {
        $.ajax({
          url: `${this.getUrl()}Notifications/StatusNotifications?__adcenterAJAX=true&id=${no.id}&cid=${
            cid
          }&uid=${uid}${aidPart}`,
          type: 'DELETE',
          timeout: 20000,
          xhrFields: {
            withCredentials: true,
          },
          success: () => {},
          error: () => null,
        });
      };
    } else {
      // eslint-disable-next-line no-param-reassign
      notification.dismissAction = _.noop;
    }
  }

  transformCCNotifications(notifications: ICCNotification[]): INotification[] {
    return notifications.map((notification) => {
      if (notification.alertType === 'BannerAlert') {
        const bannerNot: ICCBannerNotification = notification as ICCBannerNotification;
        let target: TargetComponent = TargetComponent.TopBanner;
        if (bannerNot.ActionTriggered) {
          target = TargetComponent.FloatingBanner;
        }

        return {
          severity: notificationLevelMapping[bannerNot.NotificationLevel],
          message: bannerNot.NotificationMessage,
          id: bannerNot.NotificationId,
          condition: true,
          target,
          dismissMode: DismissMode.never,
          scopeLevel: ScopeLevel.account,
        } as INotification;
      }
      const unqNot: ICCUNQNotification = notification as ICCUNQNotification;
      unqNot.target = TargetComponent.UnifiedQueue;
      unqNot.condition = true;
      unqNot.dismissMode = DismissMode.custom;
      unqNot.scopeLevel = ScopeLevel.account;
      this.buildDismissCCNotification(unqNot);

      return unqNot;
    });
  }
}
